import { Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "app/base-module/base-service/auth.service";
import { CadastroClienteSimplesComponent } from "app/clientes/cadastro-cliente-simples/cadastro-cliente-simples.component";
import { TipoInvestimento } from "app/constants";
import { FiltrarBoletaComponent } from "app/movimentacao/filtrar-boleta/filtrar-boleta.component";
import { FiltrosBoletaQueryParams } from "app/movimentacao/models/filtros-boleta-query-params.model";
import { DIALOG_CANCEL_ACTION, DialogCancelAction } from "app/shared/dialog-constants";
import { NgxSortEvent } from "app/shared/types/ngx-datatable-custom";
import { Usuario } from "app/usuarios/models/user.model";
import { Subject, takeUntil } from "rxjs";
import { ActionDialog, BaseComponent } from "../../base-module/base-component/base.component";
import { CadastroBoletaConsorcioComponent } from "../cadastro-boleta-consorcio/cadastro-boleta-consorcio.component";
import { FiltrosBoletaConsorcioQueryParams } from "../models/filtros-lista-boleta-consorcio-query-params.model";
import { BoletaConsorcioService } from "../services/boleta-consorcio.service";
import { CartaBoletaConsorcioService } from "../services/carta-boleta-consorcio.service";
import { VisualizarBoletaConsorcioComponent } from "../visualizar-boleta-consorcio/visualizar-boleta-consorcio.component";

type ConsorcioBoletaFilterKey = keyof Pick<
    FiltrosBoletaQueryParams,
    | "data_operacao_inicio"
    | "data_operacao_fim"
    | "data_atualizacao_inicio"
    | "data_atualizacao_fim"
    | "cliente_id"
    | "assessor_id"
    | "escritorio_id"
    | "status_id"
    | "acao_comercial_id"
    | "tipo_consorcio_id"
    | "tipo_lance_id"
    | "data_retorno_inicio"
    | "data_retorno_fim"
    | "parceiro_consorcio_id"
    | "operador_id"
>;

@Component({
    selector: "app-lista-boleta-consorcio",
    templateUrl: "./lista-boleta-consorcio.component.html",
    styleUrls: ["./lista-boleta-consorcio.component.scss"],
})
export class ListaBoletaConsorcioComponent extends BaseComponent {
    user: Usuario;
    private destroy$ = new Subject<void>();

    private readonly consorcioFilterKeys: ConsorcioBoletaFilterKey[] = [
        "data_operacao_inicio",
        "data_operacao_fim",
        "data_atualizacao_inicio",
        "data_atualizacao_fim",
        "cliente_id",
        "assessor_id",
        "escritorio_id",
        "status_id",
        "acao_comercial_id",
        "tipo_consorcio_id",
        "tipo_lance_id",
        "data_retorno_inicio",
        "data_retorno_fim",
        "parceiro_consorcio_id",
        "operador_id",
    ];

    constructor(
        public _service: BoletaConsorcioService,
        public _cartaService: CartaBoletaConsorcioService,
        public dialog: MatDialog,
        override snackBar: MatSnackBar,
        private _authService: AuthService,
        private route: Router,
        private activatedRoute: ActivatedRoute
    ) {
        super(dialog, snackBar);

        try {
            const strUser = this._authService.getUser();
            this.user = JSON.parse(strUser ? strUser : "{}");
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e: unknown) {
            this.user = {} as Usuario;
            this.showError("Erro ao converter o usuário");
        }
    }

    ngOnInit(): void {
        this.loadFiltersFromUrl();
        this.getAll();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Evento da paginação
     * @param {PageEvent} event
     */
    pageEvent(event: PageEvent): void {
        this.params.page = event.pageIndex + 1;
        this.params.per_page = event.pageSize;

        this.getAll();
    }

    /**
     * Consulta lista de Boleta Consórcio
     */
    getAll(): void {
        this.loadingIndicator = true;
        this._service.getAll(this.params).subscribe(
            (data) => {
                this.length = data.meta.total;
                this.rows = data.data;
                this.temp = [...this.rows];
            },
            (_error) => {
                this.loadingIndicator = false;
            },
            () => {
                this.loadingIndicator = false;
            }
        );
    }

    /**
     * Exclui Boleta Consórcio
     * @param id
     */
    delete(id: number): void {
        this.dialog.open(ActionDialog, {
            width: "250px",
            disableClose: true,
            data: {
                title: "Excluir Boleta Consórcio",
                text: "Deseja realmente excluir este item?",
                callback: () => {
                    this.dialog.closeAll();

                    this.loadingIndicator = true;
                    this._service.delete(id).subscribe(
                        (data) => {
                            this.loadingIndicator = false;
                            this.showSuccess(data.message);
                            this.getAll();
                        },
                        (error) => {
                            this.closeLoading();
                            this.showError(error.message);
                        },
                        () => {
                            this.loadingIndicator = false;
                        }
                    );
                },
            },
        });
    }

    /**
     * Modal de cadastro
     */
    openCadastro(): void {
        const dialogRef = this.dialog.open(CadastroBoletaConsorcioComponent, {
            width: "800px",
            maxHeight: "99vh",
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result !== undefined && result !== null && result !== "cancel") {
                this.showSuccess("Boleta Consórcio cadastrado com sucesso!");
                this.getAll();
            } else if (result !== undefined && result !== "cancel") {
                this.showError("Ocorreu um erro ao tentar cadastrar um novo registro.");
            }
        });
    }

    /**
     * Modal de edição
     */
    openAlterar(id: number): void {
        const dialogRef = this.dialog.open(CadastroBoletaConsorcioComponent, {
            width: "800px",
            maxHeight: "99vh",
            data: {
                id: id,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result !== undefined && result !== null && result !== "cancel") {
                this.showSuccess("Boleta Consórcio atualizado com sucesso!");
                this.getAll();
            } else if (result !== undefined && result !== "cancel") {
                this.showError("Ocorreu um erro ao tentar altera o registro");
            }
        });
    }

    /**
     * Modal de Visualizar
     */
    openVisualizar(id: number): void {
        const dialogRef = this.dialog.open(VisualizarBoletaConsorcioComponent, {
            width: "800px",
            maxHeight: "99vh",
            data: {
                id: id,
            },
        });

        dialogRef.afterClosed().subscribe((_result) => {});
    }

    /**
     * Modal de Filtro
     */
    openFiltro(): void {
        const dialogRef = this.dialog.open(FiltrarBoletaComponent, {
            width: "700px",
            maxHeight: "99vh",
            data: {
                titulo: "Filtrar Boleta",
                status_boleta: true,
                status_ordem: false,
                tipo: TipoInvestimento.CONSORCIO,
                initialFilters: this.getCurrentFilters(),
                isClearing: false,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result !== "cancel" && result !== undefined) {
                    const filtros = this.buildFiltrosFromResult(result);
                    const { sortColumn, sortOrder } = this.params;

                    this.params = {
                        page: 1,
                        per_page: this.params?.per_page || 100,
                        ...filtros,
                    };

                    if (sortColumn) {
                        this.params.sortColumn = sortColumn;
                    }
                    if (sortOrder) {
                        this.params.sortOrder = sortOrder;
                    }

                    this.persistFiltersInUrl(filtros);
                    this.paginator.firstPage();
                    this.getAll();
                }
            });
    }

    /**
     * Builds API filter params from the filter dialog result.
     */
    private buildFiltrosFromResult(
        result: Partial<Record<ConsorcioBoletaFilterKey, string | null | undefined>>
    ): Partial<Record<ConsorcioBoletaFilterKey, string>> {
        const filtros: Partial<Record<ConsorcioBoletaFilterKey, string>> = {};

        for (const key of this.consorcioFilterKeys) {
            const value = result[key];
            if (value !== undefined && value !== null && value !== "") {
                filtros[key] = value;
            }
        }

        return filtros;
    }

    /**
     * Returns the active filters to prefill the filter dialog.
     */
    private getCurrentFilters(): Partial<Record<ConsorcioBoletaFilterKey, string>> {
        return this.buildFiltrosFromResult(this.params as Partial<Record<ConsorcioBoletaFilterKey, string>>);
    }

    cadastroCliente(): void {
        const dialogRef = this.dialog.open(CadastroClienteSimplesComponent, {
            width: "600px",
            data: {
                abordagem: true,
            },
        });

        dialogRef.afterClosed().subscribe((result: DialogCancelAction | undefined | unknown) => {
            if (result === DIALOG_CANCEL_ACTION) return;

            if (result) {
                this.showSuccess("Cliente cadastrado com sucesso!");
                this.getAll();
            }
        });
    }

    /**
     * Exporta Boleta de Consórcio
     */
    export(): void {
        this.openLoading();
        this._service.export(this.params).subscribe(
            (data) => {
                const blob = new Blob([data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const url = window.URL.createObjectURL(blob);
                window.open(url, "_blank");
            },
            (_error) => {
                this.closeLoading();
            },
            () => {
                this.closeLoading();
            }
        );
    }

    /**
     * Exporta Boleta de Consórcio
     */
    cartasExport(id: number): void {
        this.openLoading();
        this._cartaService.export({ boleta_id: id }).subscribe(
            (data) => {
                const blob = new Blob([data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const url = window.URL.createObjectURL(blob);
                window.open(url, "_blank");
            },
            (_error) => {
                this.closeLoading();
            },
            () => {
                this.closeLoading();
            }
        );
    }

    onSort(event: NgxSortEvent): void {
        this.params.sortColumn = event.column.prop;
        this.params.sortOrder = event.newValue;

        this.getAll();
    }

    /**
     * Carrega filtros da URL se existirem
     */
    private loadFiltersFromUrl(): void {
        this.params = {
            page: this.params?.page || 1,
            per_page: this.params?.per_page || 100,
        };

        this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            if (Object.keys(params).length > 0) {
                // Aplicar filtros da URL aos parâmetros de busca
                this.params = {
                    ...this.params,
                    ...this.parseUrlFilters(params as FiltrosBoletaConsorcioQueryParams),
                };
            }
        });
    }

    /**
     * Converte parâmetros da URL para filtros da API
     */
    private parseUrlFilters(urlParams: FiltrosBoletaConsorcioQueryParams): Partial<FiltrosBoletaConsorcioQueryParams> {
        return this.buildFiltrosFromResult(urlParams);
    }

    /**
     * Persiste os filtros na URL como query parameters
     */
    private persistFiltersInUrl(filters: Partial<Record<ConsorcioBoletaFilterKey, string>>): void {
        this.route.navigate([], {
            queryParams: filters,
            replaceUrl: true,
        });
    }

    /**
     * Retorna o número de filtros ativos
     */
    get activeFiltersCount(): number {
        const availableParams = Object.keys(this.params ?? {}).filter(
            (param) => param !== "page" && param !== "per_page" && param !== "sortColumn" && param !== "sortOrder"
        );
        if (availableParams.length === 0) return 0;

        const filterValues = Object.keys(this.params ?? {})
            .filter(
                (param) => param !== "page" && param !== "per_page" && param !== "sortColumn" && param !== "sortOrder"
            )
            .map((field) => this.params[field as keyof typeof this.params])
            .filter((value) => value !== null && value !== undefined && value !== "" && value !== 0);

        return filterValues.length;
    }

    /**
     * Indica se há filtros ativos
     */
    get hasActiveFilters(): boolean {
        return this.activeFiltersCount > 0;
    }

    isRecorrente(is_recorrente: boolean): string {
        if (is_recorrente === null || is_recorrente === undefined) {
            return "";
        }

        return is_recorrente ? "SIM" : "NÃO";
    }
}
